import { parseCsv } from '@alexiarest/shared';

/**
 * Lectura de ficheros de importacion, comun a todos los asistentes.
 *
 * CSV se parsea directo; Excel va por SheetJS, que se carga bajo demanda
 * (chunk aparte: no queremos 500 KB en el arranque de la app por una funcion
 * que casi nadie usa el primer dia).
 */
export async function readImportFile(file: File, hojaPreferida?: string): Promise<string[][]> {
  if (!/\.(xlsx|xls)$/i.test(file.name)) {
    return parseCsv(await file.text());
  }
  const filas = await readImportFileRaw(file, hojaPreferida);
  return filas.filter((row) => row.some((cell) => cell !== ''));
}

/**
 * Como `readImportFile`, pero SIN quitar las filas vacías: `filas[i]` es la
 * fila i+1 de la hoja. Lo necesitan las plantillas de Alex, que se leen por
 * posición (cabecera en la 4, datos debajo de la línea) y avisan con el
 * número de fila de verdad.
 */
export async function readImportFileRaw(file: File, hojaPreferida?: string): Promise<string[][]> {
  if (!/\.(xlsx|xls)$/i.test(file.name)) {
    return parseCsv(await file.text());
  }
  return leerHojaCruda(await file.arrayBuffer(), hojaPreferida);
}

/** Una hoja del libro tal cual: `filas[i]` es la fila i+1 de la hoja. */
export interface HojaCruda {
  nombre: string;
  filas: string[][];
}

/**
 * Todas las hojas de un archivo, sin quitar filas vacías (24 oct 2026: el
 * importador de ventas elige la hoja y encuentra la tabla dentro). Un CSV es
 * un libro de una hoja sin nombre.
 */
export async function leerLibroCrudo(file: File): Promise<HojaCruda[]> {
  // Topes contra libros enormes o hechos a mala fe (revisión de seguridad,
  // 27 oct 2026): un export de ventas no pesa 25 MB ni tiene 200 columnas.
  if (file.size > MAX_BYTES_LIBRO) throw new Error('archivo demasiado grande');
  if (!/\.(xlsx|xls)$/i.test(file.name)) {
    return [{ nombre: '', filas: recortarFilas(parseCsv(await file.text())) }];
  }
  const XLSX = await import('xlsx');
  // sheetRows: SheetJS deja de leer ahí; una fila más que el máximo basta para avisar.
  const workbook = XLSX.read(await file.arrayBuffer(), { cellDates: true, sheetRows: MAX_FILAS_LIBRO + 1 });
  return workbook.SheetNames.slice(0, MAX_HOJAS_LIBRO).map((nombre) => ({
    nombre,
    filas: recortarFilas(celdasDeHoja(XLSX, workbook.Sheets[nombre], MAX_COLUMNAS_LIBRO, true)),
  }));
}

const MAX_BYTES_LIBRO = 25 * 1024 * 1024;
/** Una más que el máximo del importador (20.000): así se puede avisar. */
const MAX_FILAS_LIBRO = 20_000;
const MAX_HOJAS_LIBRO = 30;
const MAX_COLUMNAS_LIBRO = 200;
const MAX_CHARS_CELDA_LIBRO = 1_000;

function recortarFilas(filas: string[][]): string[][] {
  return filas
    .slice(0, MAX_FILAS_LIBRO + 1)
    .map((f) => f.slice(0, MAX_COLUMNAS_LIBRO).map((c) => c.slice(0, MAX_CHARS_CELDA_LIBRO)));
}

/** Lee una hoja de un libro de Excel ya cargado en memoria (también para los tests). */
export async function leerHojaCruda(datos: ArrayBuffer, hojaPreferida?: string): Promise<string[][]> {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(datos, { cellDates: true });

  /*
   * Con varias hojas, se coge la que toca por nombre ("Ingredientes",
   * "Recetas", "Gastos"): la plantilla de gastos trae además la hoja
   * «Categorías», y un libro viejo traía ingredientes y recetas juntos.
   */
  const nombre =
    (hojaPreferida &&
      workbook.SheetNames.find(
        (n) => n.trim().toLowerCase() === hojaPreferida.trim().toLowerCase(),
      )) ||
    workbook.SheetNames[0];
  return celdasDeHoja(XLSX, nombre ? workbook.Sheets[nombre] : undefined);
}

type Xlsx = typeof import('xlsx');

function celdasDeHoja(
  XLSX: Xlsx,
  sheet: import('xlsx').WorkSheet | undefined,
  maxColumnas?: number,
  /** Rellenar también las columnas de la izquierda, para que el índice 0 sea la A. */
  desdeLaA = false,
): string[][] {
  if (!sheet) return [];
  /*
   * Una celda suelta en la columna XFD haría filas de 16.000 celdas: se
   * recorta el rango antes de convertir, no después.
   */
  if (maxColumnas && sheet['!ref']) {
    const rango = XLSX.utils.decode_range(sheet['!ref']);
    if (rango.e.c - rango.s.c + 1 > maxColumnas) {
      rango.e.c = rango.s.c + maxColumnas - 1;
      sheet['!ref'] = XLSX.utils.encode_range(rango);
    }
  }

  // raw:true da los VALORES de celda, no el texto formateado: un numero
  // llega con punto decimal y una fecha como Date, sin ambiguedad DD/MM.
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, defval: '', blankrows: true });
  // Si la hoja no empieza en la fila 1, se rellenan las de arriba para que
  // el indice siga siendo el numero de fila.
  const primera = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']).s.r : 0;
  const izquierda = desdeLaA && sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']).s.c : 0;
  const huecos = Array.from({ length: izquierda }, () => '');
  return [
    ...Array.from({ length: primera }, () => [] as string[]),
    ...rows.map((row) => [
      ...huecos,
      ...row.map((cell) => {
        // Componentes LOCALES, no toISOString(): un Excel con 04/07/2026 a
        // medianoche en Madrid es 03/07 22:00 en UTC y cambiaria el dia.
        if (cell instanceof Date) {
          const y = cell.getFullYear();
          const m = String(cell.getMonth() + 1).padStart(2, '0');
          const d = String(cell.getDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        }
        return String(cell ?? '').trim();
      }),
    ]),
  ];
}

/**
 * Descarga una de las plantillas de Alex tal cual (con sus colores, sus
 * desplegables y sus ejemplos): son archivos estaticos de `public/plantillas`.
 */
export function descargarArchivoPublico(nombre: string): void {
  const a = document.createElement('a');
  a.href = `${import.meta.env.BASE_URL}plantillas/${encodeURIComponent(nombre)}`;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Genera y descarga la plantilla de importacion del catalogo: un libro con
 * una hoja de ingredientes y otra de recetas, con ejemplos dentro. Es mas
 * facil rellenar un ejemplo que leer una documentacion.
 */
/**
 * Descarga un libro de una sola hoja. Los numeros van como NUMEROS de celda
 * (no texto): asi Excel deja editar precios sin pelearse con el formato, y
 * al resubir el importador los lee sin ambiguedad de comas.
 */
export async function descargarExcel(
  nombreFichero: string,
  hoja: string,
  headers: string[],
  rows: Array<Array<string | number>>,
): Promise<void> {
  const XLSX = await import('xlsx');
  const libro = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  sheet['!cols'] = headers.map((h) => ({ wch: Math.max(14, h.length + 2) }));
  XLSX.utils.book_append_sheet(libro, sheet, hoja);

  const datos = XLSX.write(libro, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const url = URL.createObjectURL(
    new Blob([datos], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreFichero;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function descargarPlantillaCatalogo(hojas: {
  ingredientes: { headers: string[]; rows: string[][] };
  recetas: { headers: string[]; rows: string[][] };
}): Promise<void> {
  const XLSX = await import('xlsx');
  const libro = XLSX.utils.book_new();

  const anyade = (nombre: string, headers: string[], rows: string[][]) => {
    const hoja = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    // Un ancho decente: con columnas de 8 caracteres no se lee nada.
    hoja['!cols'] = headers.map((h) => ({ wch: Math.max(14, h.length + 2) }));
    XLSX.utils.book_append_sheet(libro, hoja, nombre);
  };

  anyade('Ingredientes', hojas.ingredientes.headers, hojas.ingredientes.rows);
  anyade('Recetas', hojas.recetas.headers, hojas.recetas.rows);

  const datos = XLSX.write(libro, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const url = URL.createObjectURL(
    new Blob([datos], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla-alexia-rest.xlsx';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Plantilla estandar de ventas: el formato que el importador entiende sin que
 * el usuario tenga que mapear nada. Se usa con `promptAdaptarVentas` para
 * convertir el export de un TPV que no encaja.
 */
export async function descargarPlantillaVentas(
  headers: string[],
  rows: string[][],
): Promise<void> {
  await descargarExcel('plantilla-ventas-alexia-rest.xlsx', 'Ventas', headers, rows);
}

/**
 * Plantilla de unidades vendidas (mejora 64 de Alex, 6 sep 2026): sale ya
 * con la carta del restaurante —codigo y nombre de cada plato— y la columna
 * de unidades vacia. Solo hay que rellenar y volver a subir.
 */
export async function descargarPlantillaUnidades(
  headers: string[],
  platos: Array<{ code?: string; name: string }>,
  periodo: string,
): Promise<void> {
  const rows = platos.map((p) => [p.code ?? '', p.name, '']);
  await descargarExcel(`plantilla-unidades-vendidas-${periodo}.xlsx`, 'Unidades', headers, rows);
}
